import React, { useEffect, useState } from "react";
import { Grid, Typography, IconButton, OutlinedInput, Box } from "@mui/material";
import Modal from "components/ui/Modal";
import Button from "../Button";
import CloseIcon from "@mui/icons-material/Close";
import CalenderComp from "features/Setting/order/components/CalenderComp";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getIcon } from "utils/iconsConfig";
import apiClient from "lib/api-client";
import { findSpecificMenu, findSpecificSubMenu, isAllowed } from "utils/checkUserRigths";
import { usePermissions } from "context/PermissionsContext";
import "./createstockordermodal.css";
import Selectinput from "features/order_client_details/components/Selectinput";
import ReOrderSettingsModal from "./ReOrderSettinsModal";
import QuickCustomerOrder from "./QuickCustomerOrder";
import DescriptionIcon from "@mui/icons-material/Description";
import { ReactComponent as YellowGold } from "../../../assets/icons/YellowGoldBar.svg";
import { ReactComponent as PinkGold } from "../../../assets/icons/RoseGoldBar.svg";
import { ReactComponent as WhiteGold } from "../../../assets/icons/WhiteGoldBar.svg";

const DIAMOND_QUALITY_OPTIONS = ["VVS-EF", "VVS-FG", "VVS-GH", "VS-GH", "VSSI-GH", "SI-HI", "SI-IJ"];
const GOLD_QUALITY_OPTIONS = [
  { label: "18 - Pink", icon: <PinkGold /> },
  { label: "18 - White", icon: <WhiteGold /> },
  { label: "18 - Yellow", icon: <YellowGold /> },
  { label: "14 - Pink", icon: <PinkGold /> },
  { label: "14 - White", icon: <WhiteGold /> },
  { label: "14 - Yellow", icon: <YellowGold /> },
];
const RHODIUM_OPTIONS = [
  { label: "White", icon: <WhiteGold /> },
  { label: "Pink", icon: <PinkGold /> },
  { label: "Yellow", icon: <YellowGold /> },
];
const PRIORITY_OPTIONS = ["Standard", "Urgent"];

export const styling = {
  color: "#000",
  border: "1px solid black",
  width: "100%",
};
const textStyle = {
  fontFamily: "Karla",
  fontWeight: 500,
  fontSize: "16px",
  lineHeight: "20px",
  letterSpacing: "0px",
};

// Consistent icon wrapper component
export const ConsistentIcon = ({ type, color }) => {
  const iconElement = getIcon(type, color);
  return React.cloneElement(iconElement, {
    style: { width: "18px", height: "18px", maxWidth: "18px", maxHeight: "18px" },
  });
};

dayjs.extend(advancedFormat);

const getTenthValidDate = () => {
  const today = new Date();
  const cursor = new Date(today);
  cursor.setDate(today.getDate() + 1);
  let count = 0;

  while (count < 10) {
    if (cursor.getDay() !== 0) {
      count++;
    }
    if (count < 10) {
      cursor.setDate(cursor.getDate() + 1);
    }
  }

  return cursor;
};

function pickBinnysJewelleryFromGati(rows) {
  if (!Array.isArray(rows)) return null;
  const normalize = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .replace(/\./g, "");
  const byName = rows.find((row) => {
    const n = normalize(row?.clientName);
    const isBinnys = n.includes("binnys");
    const isJewellery = n.includes("jewellery") || n.includes("jewelry");
    const isPvtLtd = n.includes("private limited") || n.includes("pvt ltd") || n.includes("private ltd") || n.includes("pvt limited");
    return isBinnys && isJewellery && isPvtLtd;
  });
  if (byName) return byName;
  return rows.find((row) => String(row?.partyCode || "").toUpperCase() === "BJPL") || null;
}

function CreateStockOrderModal({ open, handleClose, designData, actionType, customerQuoteData = {}, sourceType = "" }) {
  const permission = usePermissions();
  const hasAccessToYearlySalesData = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Sales By year"), "view");
  const hasAccessToClientSalesData = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Sales By Client"), "view");
  const hasSalesAccess = hasAccessToClientSalesData || hasAccessToYearlySalesData;

  const [size, setSize] = useState("");
  const [diamondQuality, setDiamondQuality] = useState("VS-GH");
  const [goldQuality, setGoldQuality] = useState("18 - Pink");
  const [rhodium, setRhodium] = useState("White");
  const [priority, setPriority] = useState("Standard");
  const [deliveryDate, setDeliveryDate] = useState(getTenthValidDate());
  const [comment, setComment] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);
  const [canSave, setCanSave] = useState(false);
  const [openReorderModal, setOpenReorderModal] = useState(false);
  const [openCutomerOrderModal, setOpenCustomerorderModal] = useState(false);
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [sizeOptions, setSizeOptions] = useState([]);
  const queryClient = useQueryClient();
  // reorderData is often a partial payload; merge so preview fields (style, weights, sales) fall back to the card row.
  const reorderPayload = designData?.reorderData;
  const cardSource = reorderPayload && typeof reorderPayload === "object" ? { ...designData, ...reorderPayload } : designData || {};
  const cardIdentifier = sourceType === "stockcard" ? cardSource?.jewelCode || "N/A" : cardSource?.styleNumber || "N/A";
  const cardDiamondText =
    cardSource?.diamond || (cardSource?.diaWt || cardSource?.diaWeight || cardSource?.diaPcs ? `${cardSource?.diaWt || cardSource?.diaWeight || 0} Ct / ${cardSource?.diaPcs || 0} Pcs` : "N/A");
  const cardGoldText =
    cardSource?.weight ||
    (cardSource?.netWt || cardSource?.netWeight || cardSource?.grossWt || cardSource?.grossWeight
      ? `${cardSource?.netWt || cardSource?.netWeight || 0} g / ${cardSource?.grossWt || cardSource?.grossWeight || 0} g`
      : "N/A");
  const cardSalesText = cardSource?.sales ?? cardSource?.styleOrderCount ?? cardSource?.orderCount ?? "N/A";

  const categoryNameForSize = designData?.categoryName || designData?.category || "";
  const initialSizeFromCard = designData?.itemSize || "";
  const sizePlaceHolder = sizeOptions?.length > 0 ? "Select size" : "N/A";
  const sizeSelectOptions = sizeOptions?.length > 0 ? sizeOptions : ["N/A"];

  const { data: quickCustomerClientOptions = [] } = useQuery({
    queryKey: ["gatiClients"],
    enabled: Boolean(open || openCutomerOrderModal),
    queryFn: async () => {
      const cacheKey = ["gatiClients"];
      const cachedClients = queryClient.getQueryData(cacheKey);
      if (Array.isArray(cachedClients) && cachedClients.length > 0) {
        return cachedClients;
      }

      const response = await apiClient.get("/api/v4/gati_client/getGatiClientsNamesDetails");
      const resData = response?.data;
      const clientData = Array.isArray(resData?.clientData) ? resData.clientData : [];

      const formattedClients = clientData
        .filter((item) => item?.clientName && item?.partyCode && item?.clientName !== "(As Per Detail)")
        .map((item) => ({
          label: `${item.clientName} (${item.partyCode})`,
          value: `${item.clientName} (${item.partyCode})`,
          clientName: item.clientName,
          partyCode: item.partyCode,
          partyNo: item.partyNo,
          firmEmail: item.firmEmail || [],
          otherFirmEmail: item.otherFirmEmail || [],
          whatsAppNo: item.whatsAppNo || [],
          otherWhatsAppNo: item.otherWhatsAppNo || [],
        }));

      queryClient.setQueryData(cacheKey, formattedClients);
      return formattedClients;
    },
    staleTime: 5 * 60 * 1000,
  });
  const uniqueQuickCustomerClientOptions = Array.from(
    new Map(
      quickCustomerClientOptions.map((item) => [
        `${String(item?.clientName || "")
          .trim()
          .toLowerCase()}-${String(item?.partyCode || "")
          .trim()
          .toUpperCase()}`,
        item,
      ])
    ).values()
  );

  const { mutateAsync: fetchSizeByCategory } = useMutation({
    mutationFn: async (categoryName) => {
      const cacheKey = ["item-sizes", categoryName];
      const cachedSizes = queryClient.getQueryData(cacheKey);
      if (Array.isArray(cachedSizes)) {
        return cachedSizes;
      }

      const response = await apiClient.get(`/api/v3/client/vision_order/getCategorySizeList/${encodeURIComponent(categoryName)}`);
      const fetchedSizes = Array.isArray(response?.data?.sizes) ? response.data.sizes : [];
      queryClient.setQueryData(cacheKey, fetchedSizes);
      return fetchedSizes;
    },
  });

  useEffect(() => {
    let isActive = true;

    const fetchSizeOptions = async () => {
      if (!open || !categoryNameForSize) {
        setSizeOptions([]);
        setSize("N/A");
        return;
      }

      try {
        const fetchedSizes = await fetchSizeByCategory(categoryNameForSize);
        if (!isActive) return;

        if (Array.isArray(fetchedSizes) && fetchedSizes.length > 0) {
          setSizeOptions(fetchedSizes);
          if (initialSizeFromCard && fetchedSizes.includes(initialSizeFromCard)) {
            setSize(initialSizeFromCard);
          } else if (!size || !fetchedSizes.includes(size)) {
            setSize("");
          }
          return;
        }
        setSizeOptions([]);
        setSize("N/A");
      } catch (error) {
        if (!isActive) return;
        setSizeOptions([]);
        setSize("N/A");
      }
    };

    fetchSizeOptions();
    return () => {
      isActive = false;
    };
  }, [open, categoryNameForSize, initialSizeFromCard, fetchSizeByCategory]);

  useEffect(() => {
    if (!sizeOptions?.length) {
      setSize("N/A");
      return;
    }
    if (size && size !== "N/A" && !sizeOptions.includes(size)) {
      setSize("");
    }
  }, [sizeOptions]);

  useEffect(() => {
    if (!open) return;
    if (!deliveryDate) {
      setDeliveryDate(getTenthValidDate());
    }
  }, [open, deliveryDate]);

  useEffect(() => {
    if (!open) return;
    setDiamondQuality("VS-GH");
    setGoldQuality("18 - Pink");
    setRhodium("White");
    setPriority("Standard");
    setComment("");
    setFile(null);
  }, [open]);

  useEffect(() => {
    if (actionType === "customer") {
      if (size && diamondQuality && goldQuality && rhodium) {
        setCanSave(true);
      } else {
        setCanSave(false);
      }
    } else if (actionType === "stock" || actionType === "reorder") {
      if (size && diamondQuality && goldQuality && rhodium && priority && deliveryDate) {
        setCanSave(true);
      } else {
        setCanSave(false);
      }
    } else {
      setCanSave(false);
    }
  }, [actionType, size, diamondQuality, goldQuality, rhodium, priority, deliveryDate, comment]);

  const clearState = () => {
    setSize("");
    setDiamondQuality("VS-GH");
    setGoldQuality("18 - Pink");
    setRhodium("White");
    setPriority("Standard");
    setDeliveryDate(getTenthValidDate());
    setComment("");
    setShowCalendar(false);
    setOpenCustomerorderModal(false);
    setOpenReorderModal(false);
    handleClose();
  };

  /** Backdrop / Escape: close without wiping fields; nested quote modals close too. */
  const dismissModal = () => {
    setShowCalendar(false);
    setOpenCustomerorderModal(false);
    setOpenReorderModal(false);
    handleClose();
  };

  const handleDateSelect = (date) => {
    setDeliveryDate(date);
    setShowCalendar(false);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Only allow images
    if (!selectedFile.type.startsWith("image/")) {
      alert("Only image files are allowed");
      return;
    }

    setFile(selectedFile);
  };

  const handleRemoveFile = () => {
    setFile(null);
  };

  const handleGenerate = async () => {
    if (!canSave || isSubmitting) return;

    const normalizeGold = (value = "") => {
      const [goldQlty = "", goldColorName = ""] = value.split(" - ");
      const colorMap = { Pink: "P", Yellow: "Y", White: "W" };

      return {
        goldQlty: goldQlty?.trim() || null,
        goldColor: colorMap[goldColorName?.trim()] || null,
      };
    };

    const goldQualitySource = customerQuoteData?.goldQuality || goldQuality || "";
    const { goldQlty: parsedGoldQlty, goldColor: parsedGoldColor } = normalizeGold(goldQualitySource);
    const rhodiumMap = { White: "W", Pink: "P", Yellow: "Y", W: "W", P: "P", Y: "Y" };
    const resolvedRhodium = rhodiumMap[rhodium] || "W";

    const useStockReorderGatiDefaults = actionType === "stock" || actionType === "reorder";
    const stockReorderGati = useStockReorderGatiDefaults ? pickBinnysJewelleryFromGati(uniqueQuickCustomerClientOptions) : null;
    const stockReorderEmails = stockReorderGati ? [...(stockReorderGati.firmEmail || []), ...(stockReorderGati.otherFirmEmail || [])].filter((email) => email?.trim()) : [];
    const stockReorderPhones = stockReorderGati
      ? [...(stockReorderGati.whatsAppNo || []), ...(stockReorderGati.otherWhatsAppNo || [])]
          .filter((phone) => {
            const cleanPhone = phone?.replace(/^\+91|^91/, "").trim();
            return cleanPhone && cleanPhone.length >= 10;
          })
          .map((phone) => String(phone).replace(/^\+91|^91/, ""))
      : [];

    const payload = {
      clientName: useStockReorderGatiDefaults ? stockReorderGati?.clientName || "BJPL" : "",
      partyNo: useStockReorderGatiDefaults ? (stockReorderGati?.partyNo ?? "") : "",
      partyCode: useStockReorderGatiDefaults ? stockReorderGati?.partyCode || "BJPL" : "",
      purchausOrder: "",
      bookType: "B2B Quote",
      certificate: "None",
      priority,
      deliveryDate: deliveryDate ? dayjs(deliveryDate).format("DD/MM/YYYY") : "",
      remarks: comment || "",
      stampInstruction: "",

      designs: [
        {
          id: designData?.id || designData?._id || "",
          styleNumber: designData?.styleNumber || "",
          itemSize: size && size !== "N/A" ? size : designData?.itemSize && String(designData?.itemSize) !== "N/A" ? designData.itemSize : null,
          diaQlty: diamondQuality || designData?.diaQlty || null,
          goldQlty: parsedGoldQlty || designData?.goldQlty || null,
          goldColor: parsedGoldColor || designData?.goldColor || null,
          message: comment || "",
          rhodium: resolvedRhodium,
          itemPoNo: "",
        },
      ],
      phoneNumbers: useStockReorderGatiDefaults ? stockReorderPhones : [],
      emails: useStockReorderGatiDefaults ? stockReorderEmails : [],
    };

    try {
      setIsSubmitting(true);

      if (actionType === "reorder") {
        setOpenReorderModal(true);
        return;
      }

      if (actionType === "customer") {
        setOpenCustomerorderModal(true);
        return;
      }

      await apiClient.post("/api/v3/client/vision_order/createVisionOrder", payload);

      toast.success("Order created successfully", { autoClose: 1200 });

      clearState();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to create order", { autoClose: 2000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPriorityColor = () => {
    if (priority === "Standard") return "#5078C8";
    if (priority === "Urgent") return "#DE9300";
    return "#9e9e9e";
  };

  return (
    <>
      <Modal
        open={open}
        handleClose={dismissModal}
        style={{
          width: "25vw",
          position: "fixed",
          maxHeight: "96vh",
          padding: "0px 6px 16px 18px", 
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", maxHeight: "88vh", overflow: "hidden" }}>
          {/* HEADER */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              background: "#fff",
              padding: "10px 0",
            }}
          >
            <Grid container>
              <Grid item xs={12} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography className="sco-select-header">{actionType === "customer" ? "Quick Customer Quote" : actionType === "reorder" ? "Quick Re-Quote" : "Quick Stock Quote"}</Typography>

                <IconButton onClick={clearState} size="small">
                  <CloseIcon sx={{ fontSize: "1.2vw", color: "#444" }} />
                </IconButton>
              </Grid>
            </Grid>
          </Box>

          {/* SCROLLABLE CONTENT */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              paddingRight: "4px",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-button": {
                display: "none",
              },
              "&::-webkit-scrollbar-track": {
                background: "#424242",
                borderRadius: "0px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#9e9e9e",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#bdbdbd",
              },
            }}
          >
            <Grid container spacing={"1.1vw"}>
              {/* ── Product Card ── */}
              <Grid item xs={12}>
                <div
                  style={{
                    width: "100%",
                    maxWidth: "26vw",
                    minHeight: "180px",
                    borderRadius: "5px",
                    padding: "10px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: "rgba(26, 26, 26, 1)",
                    color: "#fff",
                    gap: "10px",
                    boxSizing: "border-box",
                  }}
                >
                  {/* LEFT SECTION */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      gap: "1.3vw",
                      flex: 1,
                    }}
                  >
                    {/* COMMON ICON STYLE */}
                    {(() => {
                      const iconWrapStyle = {
                        width: "28px",
                        height: "28px",
                        minWidth: "28px",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#1c1f26",
                      };

                      const iconStyle = {
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      };

                      return (
                        <>
                          {/* Diamond */}
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={iconWrapStyle}>
                              <ConsistentIcon type="BlueRingIcon" />
                            </span>
                            <span style={textStyle}>{cardIdentifier}</span>
                          </div>

                          {/* Weight */}
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={iconWrapStyle}>
                              <ConsistentIcon type="BlueDiamond" color="#00c853" />
                            </span>
                            <span style={textStyle}>{cardDiamondText}</span>
                          </div>

                          {/* Stone */}
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={iconWrapStyle}>
                              <ConsistentIcon type="GoldBarBreadcrumb" color="#00c853" />
                            </span>
                            <span style={textStyle}>{cardGoldText}</span>
                          </div>

                          {/* Sales — only with rights; omit row so flex gap does not leave a blank band (card minHeight unchanged) */}
                          {hasSalesAccess ? (
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <span style={iconWrapStyle}>
                                <span style={{ ...iconStyle, color: "#9e9e9e" }}>
                                  <ConsistentIcon type="sales" />
                                </span>
                              </span>
                              <span style={{ ...textStyle, color: "#ccc" }}>{cardSalesText}</span>
                            </div>
                          ) : null}
                        </>
                      );
                    })()}
                  </div>

                  {/* RIGHT IMAGE */}
                  <div
                    style={{
                      width: "100%",
                      maxWidth: "11vw",
                      aspectRatio: "1 / 1",
                      borderRadius: "5px",
                      overflow: "hidden",
                      background: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {designData?.imageUrl || designData?.Orgimg ? (
                      <img
                        src={designData?.imageUrl || designData?.Orgimg}
                        alt={designData?.styleNumber}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <div style={{ width: "100%", height: "100%", background: "#eee" }} />
                    )}
                  </div>
                </div>
              </Grid>

              {/* ── Size ── */}
              {actionType === "customer" ? (
                <>
                  <Grid item xs={12} className="sco-main-grid-item">
                    <div className="sco-label-row">
                      <span className="sco-label-icon">
                        <ConsistentIcon type="scale" color="black" />
                      </span>
                      <Typography className="sco-select-header-create">Size</Typography>
                    </div>
                    <Selectinput
                      value={size || ""}
                      onchange={(e) => setSize(e.target.value)}
                      options={sizeSelectOptions}
                      useMuiArrow={true}
                      placeHolder={sizePlaceHolder}
                      centerOptions={true}
                      customStyle={{
                        ...styling,
                        "& .MuiSelect-select": {
                          color: size ? "#000" : "#9e9e9e",
                          WebkitTextFillColor: size ? "#000" : "#9e9e9e",
                        },
                      }}
                    />
                  </Grid>

                  {/* ── Diamond Quality ── */}
                  <Grid item xs={12} className="sco-main-grid-item">
                    <div className="sco-label-row">
                      <span className="sco-label-icon">
                        <ConsistentIcon type="diamond" color="black" />
                      </span>
                      <Typography className="sco-select-header-create">Diamond Quality</Typography>
                    </div>
                    <Selectinput
                      value={diamondQuality}
                      onchange={(e) => setDiamondQuality(e.target.value)}
                      options={DIAMOND_QUALITY_OPTIONS}
                      useMuiArrow={true}
                      placeHolder="Select diamond quality"
                      centerOptions={true}
                      customStyle={{
                        ...styling,
                        "& .MuiSelect-select": {
                          color: diamondQuality ? "#000" : "#9e9e9e",
                          WebkitTextFillColor: diamondQuality ? "#000" : "#9e9e9e",
                        },
                      }}
                    />
                  </Grid>

                  {/* ── Gold Quality ── */}
                  <Grid item xs={12} className="sco-main-grid-item">
                    <div className="sco-label-row">
                      <span className="sco-label-icon">
                        <ConsistentIcon type="goldCartOrder" color="black" />
                      </span>
                      <Typography className="sco-select-header-create">Gold Quality</Typography>
                    </div>
                    <Selectinput
                      value={goldQuality}
                      onchange={(e) => setGoldQuality(e.target.value)}
                      options={GOLD_QUALITY_OPTIONS}
                      useMuiArrow={true}
                      placeHolder="Select gold quality"
                      centerOptions={true}
                      customStyle={{
                        ...styling,
                        "& .MuiSelect-select": {
                          color: goldQuality ? "#000" : "#9e9e9e",
                          WebkitTextFillColor: goldQuality ? "#000" : "#9e9e9e",
                        },
                      }}
                    />
                  </Grid>

                  {/* ── Rhodium ── */}
                  <Grid item xs={12} className="sco-main-grid-item">
                    <div className="sco-label-row">
                      <span className="sco-label-icon">
                        <ConsistentIcon type="RhoidumIcon" />
                      </span>
                      <Typography className="sco-select-header-create">Rhodium</Typography>
                    </div>
                    <Selectinput
                      value={rhodium}
                      onchange={(e) => setRhodium(e.target.value)}
                      options={RHODIUM_OPTIONS}
                      useMuiArrow={true}
                      placeHolder="Select rhodium"
                      centerOptions={true}
                      customStyle={{
                        ...styling,
                        "& .MuiSelect-select": {
                          color: rhodium ? "#000" : "#9e9e9e",
                          WebkitTextFillColor: rhodium ? "#000" : "#9e9e9e",
                        },
                      }}
                    />
                  </Grid>

                  {/* ── Comment ── */}
                  <Grid item xs={12} className="sco-main-grid-item">
                    <textarea placeholder="Enter your message" value={comment} onChange={(e) => setComment(e.target.value)} className="sco-comment-box" />
                  </Grid>

                  {/* ── Upload Box ── */}
                  <Grid item xs={12} className="sco-main-grid-item">
                    <Box
                      onClick={() => !file && document.getElementById("upload-file-input").click()}
                      sx={{
                        border: file ? "2px dashed #50C878" : "2px dashed #9e9e9e",
                        borderRadius: "8px",
                        padding: file ? "12px" : "6px",
                        cursor: "pointer",
                        backgroundColor: "#fafafa",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <input
                        type="file"
                        id="upload-file-input"
                        style={{ display: "none" }}
                        accept="image/*"
                        onChange={(e) => {
                          const selectedFile = e.target.files[0];
                          if (selectedFile && selectedFile.type.startsWith("image/")) {
                            setFile(selectedFile);
                          } else {
                            alert("Only image files are allowed");
                            e.target.value = "";
                          }
                        }}
                      />

                      {!file ? (
                        <Typography
                          sx={{
                            textAlign: "center",
                            fontSize: "20px",
                            color: "#9e9e9e",
                            fontFamily: "karla",
                            padding: "10px",
                          }}
                        >
                          Upload file
                        </Typography>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          {/* Left */}
                          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <Box
                              sx={{
                                width: "40px",
                                height: "40px",
                                borderRadius: "10px",
                                background: "#50C878",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#fff",
                                fontSize: "18px",
                              }}
                            >
                              <DescriptionIcon sx={{ color: "#ffffffff", fontSize: "20px" }} />
                            </Box>

                            <Box>
                              <Typography sx={{ fontWeight: 500 }}>{file.name}</Typography>
                              <Typography sx={{ fontSize: "12px", color: "#666" }}>{(file.size / 1024).toFixed(2)} KB</Typography>
                            </Box>
                          </Box>

                          {/* Remove */}
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              setFile(null);
                            }}
                            sx={{
                              cursor: "pointer",
                              background: "#ddd",
                              borderRadius: "50%",
                              width: "28px",
                              height: "28px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            ✕
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </>
              ) : (
                <>
                  {/* ── Size ── */}
                  <Grid item xs={12} className="sco-main-grid-item">
                    <div className="sco-label-row">
                      <span className="sco-label-icon">
                        <ConsistentIcon type="scale" color="black" />
                      </span>
                      <Typography className="sco-select-header-create">Size</Typography>
                    </div>
                    <Selectinput
                      value={size || ""}
                      onchange={(e) => setSize(e.target.value)}
                      options={sizeSelectOptions}
                      centerOptions={true}
                      useMuiArrow={true}
                      placeHolder={sizePlaceHolder}
                      customStyle={{
                        ...styling,
                        "& .MuiSelect-select": {
                          color: size ? "#000" : "#9e9e9e",
                          WebkitTextFillColor: size ? "#000" : "#9e9e9e",
                        },
                      }}
                    />
                  </Grid>

                  {/* ── Diamond Quality ── */}
                  <Grid item xs={12} className="sco-main-grid-item">
                    <div className="sco-label-row">
                      <span className="sco-label-icon">
                        <ConsistentIcon type="diamond" color="black" />
                      </span>
                      <Typography className="sco-select-header-create">Diamond Quality</Typography>
                    </div>
                    <Selectinput
                      value={diamondQuality}
                      onchange={(e) => setDiamondQuality(e.target.value)}
                      options={DIAMOND_QUALITY_OPTIONS}
                      useMuiArrow={true}
                      centerOptions={true}
                      placeHolder="Select diamond quality"
                      customStyle={{
                        ...styling,
                        "& .MuiSelect-select": {
                          color: diamondQuality ? "#000" : "#9e9e9e",
                          WebkitTextFillColor: diamondQuality ? "#000" : "#9e9e9e",
                        },
                      }}
                    />
                  </Grid>

                  {/* ── Gold Quality ── */}
                  <Grid item xs={12} className="sco-main-grid-item">
                    <div className="sco-label-row">
                      <span className="sco-label-icon">
                        <ConsistentIcon type="goldCartOrder" color="black" />
                      </span>
                      <Typography className="sco-select-header-create">Gold Quality</Typography>
                    </div>
                    <Selectinput
                      value={goldQuality}
                      onchange={(e) => setGoldQuality(e.target.value)}
                      options={GOLD_QUALITY_OPTIONS}
                      useMuiArrow={true}
                      placeHolder="Select gold quality"
                      centerOptions={true}
                      customStyle={{
                        ...styling,
                        "& .MuiSelect-select": {
                          color: goldQuality ? "#000" : "#9e9e9e",
                          WebkitTextFillColor: goldQuality ? "#000" : "#9e9e9e",
                        },
                      }}
                    />
                  </Grid>

                  {/* ── Rhodium ── */}
                  <Grid item xs={12} className="sco-main-grid-item">
                    <div className="sco-label-row">
                      <span className="sco-label-icon">
                        <ConsistentIcon type="RhoidumIcon" />
                      </span>
                      <Typography className="sco-select-header-create">Rhodium</Typography>
                    </div>
                    <Selectinput
                      value={rhodium}
                      onchange={(e) => setRhodium(e.target.value)}
                      options={RHODIUM_OPTIONS}
                      useMuiArrow={true}
                      placeHolder="Select rhodium"
                      centerOptions={true}
                      customStyle={{
                        ...styling,
                        "& .MuiSelect-select": {
                          color: rhodium ? "#000" : "#9e9e9e",
                          WebkitTextFillColor: rhodium ? "#000" : "#9e9e9e",
                        },
                      }}
                    />
                  </Grid>

                  {/* ── Priority ── */}
                  <Grid item xs={12} className="sco-main-grid-item">
                    <div className="sco-label-row">
                      <span className="sco-label-icon">
                        <ConsistentIcon type="PriorityOrderIcon" />
                      </span>
                      <Typography className="sco-select-header-create">Priority</Typography>
                    </div>
                    <Selectinput
                      value={priority}
                      onchange={(e) => setPriority(e.target.value)}
                      centerOptions={true}
                      options={PRIORITY_OPTIONS}
                      useMuiArrow={true}
                      placeHolder="Select priority"
                      customStyle={{
                        ...styling,
                        "& .MuiSelect-select": {
                          color: getPriorityColor(),
                          WebkitTextFillColor: getPriorityColor(),
                        },
                      }}
                    />
                  </Grid>

                  {/* ── Delivery Date ── */}
                  <Grid item xs={12} className="sco-main-grid-item">
                    <div className="sco-label-row">
                      <span className="sco-label-icon">
                        <ConsistentIcon type="date" color="black" />
                      </span>
                      <Typography className="sco-select-header-create">Delivery Date</Typography>
                    </div>
                    <OutlinedInput
                      value={deliveryDate ? dayjs(deliveryDate).format("Do MMM YYYY") : ""}
                      onClick={() => setShowCalendar(true)}
                      readOnly
                      className="sco-date-input"
                      placeholder="Select delivery date"
                      sx={{
                        color: deliveryDate ? "#000" : "#9e9e9e",
                        "& input::placeholder": {
                          color: "#9e9e9e",
                          opacity: 1,
                        },
                      }}
                    />
                  </Grid>

                  {/* ── Comment ── */}
                  <Grid item xs={12} className="sco-main-grid-item">
                    <textarea placeholder="Enter your message" value={comment} onChange={(e) => setComment(e.target.value)} className="sco-comment-box" />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>

          {/* FOOTER BUTTON */}
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              background: "#fff",
              paddingTop: "10px",
              zIndex: 10,
              paddingRight: "10px",
            }}
          >
            <Button
              variant="contained"
              onClick={handleGenerate}
              sx={{
                backgroundColor: canSave ? "#50C878" : "#444444",
                "&:hover": { backgroundColor: canSave ? "#3db562" : "#333" },
                width: "100%",
                height: "6.9vh",
                borderRadius: "5px",
                fontSize: "18px",
                textTransform: "none",
                boxShadow: "none",
              }}
            >
              {actionType === "stock" ? "Generate Quote" : "Proceed"}
            </Button>
          </Box>

          {/* CALENDAR */}
          <CalenderComp
            open={showCalendar}
            handleClose={() => setShowCalendar(false)}
            onDateSelect={handleDateSelect}
            disableRestrictedDates={true}
            autoSelect={true}
            compact={true}
            isCreateProject={false}
          />
        </Box>
      </Modal>
      <ReOrderSettingsModal
        open={openReorderModal}
        handleClose={() => setOpenReorderModal(false)}
        designData={designData}
        actionType={actionType}
        onGenerateSuccess={clearState}
        reorderMeta={{
          diamondQuality,
          goldQuality,
          rhodium,
          priority,
          deliveryDate,
        }}
      />

      <QuickCustomerOrder
        open={openCutomerOrderModal}
        handleClose={() => setOpenCustomerorderModal(false)}
        onGenerateSuccess={clearState}
        clientOptions={uniqueQuickCustomerClientOptions}
        designData={designData}
        customerQuoteData={{
          size,
          diamondQuality,
          goldQuality,
          rhodium,
          comment,
        }}
      />
    </>
  );
}

export default CreateStockOrderModal;
